import './admin.css';
import { supabase } from '../lib/supabase';
import { escapeHtml, toast } from '../lib/ui';

// מסך ניהול משתמשים (מוקאפ מסך 7) — לאדמין בלבד.
// ה-RLS (litebite_is_admin) נותן לאדמין SELECT+UPDATE על כל litebite_users_access;
// האימייל בעמודת email (מיגרציה 003). "דחה" לא קיים ב-MVP — מחיקת משתמש
// דורשת service role, ולכן אין כפתור דחייה בכלל.

interface AccessRow {
  user_id: string;
  email: string | null;
  is_approved: boolean;
  is_admin: boolean;
  created_at: string;
}

const timeFmt = new Intl.DateTimeFormat('he-IL', { hour: '2-digit', minute: '2-digit' });
const dateFmt = new Intl.DateTimeFormat('he-IL', { day: '2-digit', month: '2-digit' });

function isToday(d: Date): boolean {
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

/** "נרשם היום · 14:20" / "נרשם 19.07 · 14:20" */
function signupLabel(createdAt: string): string {
  const d = new Date(createdAt);
  return `נרשם ${isToday(d) ? 'היום' : dateFmt.format(d)} · ${timeFmt.format(d)}`;
}

// ---------- רינדור ----------

export function renderAdmin(): string {
  return `
    <div class="admin">
      <div class="admin-head">
        <h2>ניהול משתמשים 🔑</h2>
        <div class="muted">הרשמה פתוחה — אבל אף אחד לא נכנס בלי אישורך</div>
      </div>
      <div id="admin-body">
        <div class="card admin-loading">
          <div class="admin-skel"></div>
          <div class="admin-skel short"></div>
        </div>
      </div>
    </div>`;
}

function pendingRow(u: AccessRow): string {
  return `
    <div class="user-row">
      <div class="ua">🙋</div>
      <div class="un">
        <b>${escapeHtml(u.email ?? 'ללא אימייל')}</b>
        <span>${signupLabel(u.created_at)}</span>
      </div>
      <button class="btn-approve" data-approve="${escapeHtml(u.user_id)}">אשר ✓</button>
    </div>`;
}

function activeRow(u: AccessRow, myId: string | null): string {
  const me = u.user_id === myId;
  return `
    <div class="user-row">
      <div class="ua">${u.is_admin ? '🔑' : '😊'}</div>
      <div class="un">
        <b>${escapeHtml(u.email ?? 'ללא אימייל')}${me ? ' (אתה)' : ''}${u.is_admin ? '<i class="tag-admin">מנהל</i>' : ''}</b>
        <span>הצטרפות: ${dateFmt.format(new Date(u.created_at))}</span>
      </div>
      <div class="status">פעיל</div>
    </div>`;
}

function bodyHtml(rows: AccessRow[], myId: string | null): string {
  const pending = rows.filter((r) => !r.is_approved);
  const active = rows.filter((r) => r.is_approved);
  const pendingHtml = pending.length
    ? pending.map(pendingRow).join('')
    : `<div class="muted admin-none">אין ממתינים כרגע 🎉</div>`;
  const activeHtml = active.length
    ? active.map((u) => activeRow(u, myId)).join('')
    : `<div class="muted admin-none">אין עדיין משתמשים פעילים</div>`;
  return `
    <div class="card">
      <b class="admin-card-title">ממתינים לאישורך (${pending.length})</b>
      ${pendingHtml}
      <div class="muted admin-note">בלי אישור אין גישה ליומן ולמאמנת — שומר גם על מכסת ה-AI החינמית.</div>
    </div>
    <div class="card">
      <b class="admin-card-title">משתמשים פעילים</b>
      ${activeHtml}
    </div>`;
}

// ---------- נתונים ----------

async function fetchAllAccess(): Promise<AccessRow[]> {
  const { data, error } = await supabase
    .from('litebite_users_access')
    .select('user_id, email, is_approved, is_admin, created_at')
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as AccessRow[];
}

async function approveUser(userId: string): Promise<void> {
  // .select() אחרי ה-update — כדי לוודא שהשורה באמת עודכנה
  // (בלי הרשאת אדמין ה-RLS פשוט מחזיר 0 שורות, בלי שגיאה)
  const { data, error } = await supabase
    .from('litebite_users_access')
    .update({ is_approved: true })
    .eq('user_id', userId)
    .select('user_id');
  if (error) throw error;
  if (!data?.length) throw new Error('not_updated');
}

// ---------- טעינה וחיבור ----------

async function refresh(root: HTMLElement): Promise<void> {
  const body = root.querySelector<HTMLElement>('#admin-body');
  if (!body) return;
  try {
    // getSession (מקומי, מיידי) ולא getUser (סבב רשת לשרת האימות) — זה מה שגרם לדיליי
    // הניכר בפתיחת המסך; ה-id דרוש רק לתיוג "(אתה)" בשורה של המשתמש הנוכחי.
    const [rows, { data: { session } }] = await Promise.all([
      fetchAllAccess(),
      supabase.auth.getSession(),
    ]);
    if (!root.isConnected) return;
    body.innerHTML = bodyHtml(rows, session?.user.id ?? null);
    attachBody(root);
  } catch {
    if (!root.isConnected) return;
    body.innerHTML = `
      <div class="card admin-error">
        <p class="muted">לא הצלחנו לטעון את רשימת המשתמשים — בדוק חיבור.</p>
        <button class="btn-primary" id="admin-retry">ניסיון חוזר</button>
      </div>`;
    body.querySelector('#admin-retry')?.addEventListener('click', () => void refresh(root));
  }
}

function attachBody(root: HTMLElement): void {
  root.querySelectorAll<HTMLButtonElement>('[data-approve]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      btn.disabled = true;
      try {
        await approveUser(btn.dataset.approve!);
      } catch {
        btn.disabled = false;
        toast('האישור נכשל — נסה שוב', 'error');
        return;
      }
      toast('אושר ✓ — המשתמש עבר לפעילים');
      void refresh(root);
    });
  });
}

export function attachAdmin(root: HTMLElement): void {
  void refresh(root);
}
